import { message } from "antd";
import { Component } from "react";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { setLoadingAction } from "utilities/form/form";

import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";

import { type CategoryType } from "../common/categoryType";
import { CategoryDetailHandler, type ICategoryDetailHandler } from "./Category.api.handler";
import {
    type CategoryEntity,
    type ICategoryDetails,
    type ILeadActivityCategoryDetails,
} from "./Category.api.types";
import {
    type CategoryFormActions,
    type CategoryFormValues,
    CategoryPresentationalForm,
} from "./CategoryPresentational";

interface ICategoryDetailProps {
    id: number;
    categoryType: CategoryType;
    allowDeleteExistingRefs: boolean;
    onAdd: (category?: CategoryEntity) => void;
    onEdit: (category?: CategoryEntity) => void;
    onDelete: (id: CategoryEntity["id"]) => void;
    handler: ICategoryDetailHandler;
    beforeClose: () => void;
}

interface ICategoryDetailState {
    categoryDetail?: ICategoryDetails | ILeadActivityCategoryDetails;
    actionBeingPerformed: CategoryFormActions;
}

export class CategoryDetail extends Component<ICategoryDetailProps, ICategoryDetailState> {
    static defaultProps = {
        allowDeleteExistingRefs: false,
        handler: new CategoryDetailHandler(),
        onAdd: () => {},
        onEdit: () => {},
        onDelete: () => {},
    };

    async componentDidMount() {
        try {
            const categoryDetail = await this.props.handler.get(
                this.categoryId,
                this.props.categoryType,
            );
            this.setState({ categoryDetail });
        } catch (e: any) {
            this.setState(() => {
                throw e;
            });
        }
    }

    private get categoryId() {
        return this.props.id >= 0 ? this.props.id : 0;
    }

    private onSubmit = async (formValues: CategoryFormValues) => {
        let success = false;
        await setLoadingAction<CategoryFormActions>(this, {
            actionBeingPerformed: "save",
            callback: async () => {
                const category = {
                    id: this.categoryId,
                    categoryType: this.props.categoryType,
                    title: formValues.title,
                    categoryColor: formValues.categoryColor,
                    categoryIcon: formValues.categoryIcon,
                };
                try {
                    const savedCategory =
                        category.id > 0
                            ? await this.handleUpdate(category)
                            : await this.handleInsert(category);
                    this.setState((prev) => ({
                        categoryDetail: {
                            ...prev.categoryDetail!,
                            id: savedCategory.id,
                            categoryTitle: savedCategory.title,
                        },
                    }));
                    success = true;
                } catch (error) {
                    showAPIErrorMessage(error);
                }
            },
        });
        return success;
    };
    private onDelete = async () => {
        await setLoadingAction<CategoryFormActions>(this, {
            actionBeingPerformed: "delete",
            callback: async () => {
                try {
                    await this.props.handler.delete(
                        this.categoryId,
                        this.props.categoryType,
                        this.props.allowDeleteExistingRefs,
                    );
                    void message.success(
                        `Deleted '${
                            this.state.categoryDetail!.categoryTitle
                        }' ${this.state.categoryDetail!.categoryFriendlyName.toLowerCase()}`,
                    );
                    this.props.onDelete(this.categoryId);
                } catch (error) {
                    showAPIErrorMessage(error);
                }
            },
        });
    };

    private handleInsert = async (category: CategoryEntity) => {
        const newCategory = await this.props.handler.insert(category);
        void message.success(
            `Added '${
                newCategory.title
            }' ${this.state.categoryDetail!.categoryFriendlyName.toLowerCase()}`,
        );
        this.props.onAdd(newCategory);
        return newCategory;
    };

    private handleUpdate = async (category: CategoryEntity) => {
        const updatedCategory = await this.props.handler.update(category);
        void message.success(
            `Updated '${this.state.categoryDetail!.categoryTitle}' to '${updatedCategory.title}'`,
        );
        this.props.onEdit(updatedCategory);
        return updatedCategory;
    };

    render() {
        // Show loading spinner until entity is loaded
        if (!this.state || !this.state.categoryDetail) {
            return <BTLoading />;
        }

        const isNew = this.props.id === 0;
        const categoryName = this.state.categoryDetail.categoryFriendlyName;
        const modalTitlePrefix = isNew ? "Add" : "Edit";

        return (
            <BTModal
                data-testid="btModalCategoryDetails"
                title={`${modalTitlePrefix} ${categoryName}`}
                visible={true}
                beforeClose={this.props.beforeClose}
                width="510px"
                removeBodyPadding={true}
                useModalLayout
            >
                <CategoryPresentationalForm
                    categoryDetail={this.state.categoryDetail}
                    categoryType={this.props.categoryType}
                    actionBeingPerformed={this.state.actionBeingPerformed}
                    onSubmit={this.onSubmit}
                    onDelete={this.onDelete}
                    title={`${modalTitlePrefix} ${categoryName}`}
                    modalConfig={{ beforeClose: this.props.beforeClose, parentRoute: "" }}
                />
            </BTModal>
        );
    }
}
